import { DateTime } from 'luxon'
import type { Building as BuildingApiSchema } from '../../shared/api/generated/models/building'
import { Address, addressFromDto } from '../../addresses/models/address'
import { BuildingCustomFields } from '../../shared/api/generated/models'
import { Capability } from '#.shared/api/generated/zod/schemas/capability.zod.ts'

export interface BuildingDisplaySingleFieldConfig<T> {
    type: 'field'
    value: keyof T
}

export interface BuildingDisplayExpressionFieldConfig {
    type: 'expression'
    value: string
}

export type BuildingDisplayFieldConfig<T> = BuildingDisplaySingleFieldConfig<T> | BuildingDisplayExpressionFieldConfig

export const defaultDisplayNameConfig: BuildingDisplaySingleFieldConfig<Building> = {
    type: 'field',
    value: 'name'
}

export const buildingNestedValue = (obj: unknown, path: string): string | undefined => {
    if (typeof obj !== 'object' || obj === null) {
        return undefined
    }

    const keys = path.split('.')
    let currentValue: unknown = obj
    for (const key of keys) {
        if (!currentValue || typeof currentValue !== 'object' || !(key in currentValue)) {
            return undefined
        }

        currentValue = (currentValue as Record<string, unknown>)[key]
    }

    return typeof currentValue === 'string' ? currentValue : undefined
}

export const buildingDisplayName = (building: Building, config: BuildingDisplayFieldConfig<Building> = defaultDisplayNameConfig): string => {
    if (config.type === 'field') {
        return building[config.value] as string
    }

    if (config.type === 'expression') {
        return config.value.replace(/\${([^}]+)}/g, (_, key) => {
            const value = buildingNestedValue(building, key)
            return value !== undefined ? value : ''
        })
    }

    return building.name
}

declare const BuildingIDSymbol: unique symbol
export type BuildingID = string & { [BuildingIDSymbol]: never }

export interface Building {
    id: BuildingID
    name: string

    createdAt: DateTime
    updatedAt: DateTime
    archivedAt: DateTime | null

    areaCount: number

    address: Address | null
    addressFull: string | null

    customFields: BuildingCustomFields

    actions?: string[]

    capabilities: Record<string, Capability>
}

export const createBuilding = (
    params: {
        id: BuildingID,
        name: string,
        createdAt: DateTime,
        updatedAt: DateTime,
        archivedAt: DateTime | null,

        areaCount: number,

        address: Address | null,

        customFields?: Record<string, string>,

        actions?: string[],

        capabilities: Record<string, Capability>,
    }
): Building => ({
    id: params.id,
    name: params.name,
    createdAt: params.createdAt,
    updatedAt: params.updatedAt,
    archivedAt: params.archivedAt,

    areaCount: params.areaCount,

    address: params.address,
    addressFull: params.address
        ? [
            params.address.addressLine1,
            params.address.addressLine2,
            params.address.city,
            params.address.postCode,
            params.address.state,
            params.address.country,
        ].filter((part) => !!part).join(', ')
        : null,

    customFields: params.customFields ?? {},

    actions: params.actions,

    capabilities: params.capabilities,
})

export const buildingFromDto = (dto: BuildingApiSchema): Building => (createBuilding({
    id: dto.id as BuildingID,
    name: dto.name,
    createdAt: DateTime.fromISO(dto.created_at),
    updatedAt: DateTime.fromISO(dto.updated_at),
    archivedAt: dto.archived_at ? DateTime.fromISO(dto.archived_at) : null,

    areaCount: dto.area_count,

    address: dto.address?.data ? addressFromDto(dto.address.data) : null,

    customFields: dto.custom_fields ?? {},

    actions: dto.actions,

    capabilities: dto.capabilities,
}))
